import * as Sentry from '@sentry/nextjs';
import { useQuery } from '@tanstack/react-query';
import { useCallback } from 'react';

import { v1Api } from '@/api/backendApi';
import {
  applyPricingOverridesToApiResponse,
  usePricingCohort,
} from '@/lib/pricing-test';
import { pricingKeys } from '@/utils/queryKeys';

import type { UseQueryOptions } from '@tanstack/react-query';

/**
 * Represents currency specific pricing for a subscription plan
 */
export interface CurrencyPricing {
  monthly: number;
  yearly: number;
}

/**
 * Represents pricing across all supported currencies
 */
export interface PricingByCurrency {
  aud: CurrencyPricing;
  brl: CurrencyPricing;
  cad: CurrencyPricing;
  eur: CurrencyPricing;
  gbp: CurrencyPricing;
  inr: CurrencyPricing;
  usd: CurrencyPricing;
}

/**
 * Represents a subscription pricing option
 */
export interface PricingOption {
  /** List of benefits included in this pricing tier (formatted as markdown strings) */
  benefits: string[];
  /** ISO timestamp when the pricing option was created */
  created: string;
  /** Short description of the pricing tier */
  description: string;
  /** Unique identifier for the pricing option */
  id: number;
  /** Whether this is the featured/recommended pricing option */
  is_popular: boolean;
  /** Pricing details for all supported currencies */
  pricing: PricingByCurrency;
  /** Matching role for the pricing tier */
  role: string;
  /** Stripe plan ID for this subscription (-1 for free plans) */
  subscription_stripe_plan_id: number;
  /** Display title of the pricing tier */
  title: string;
  /** ISO timestamp when the pricing option was last updated */
  updated: string;
}

const getPricingOptions = async (): Promise<PricingOption[]> => {
  try {
    const response = await v1Api.get<PricingOption[]>('/admin/pricing');

    return response.data;
  } catch (error) {
    Sentry.captureException(error, {
      tags: { feature: 'pricing' },
      extra: { endpoint: '/admin/pricing' },
    });

    throw error;
  }
};

/**
 * Query configuration for pricing options.
 * Shared between SSR prefetch and the client hook.
 */
export const pricingQuery: UseQueryOptions<
  PricingOption[],
  Error,
  PricingOption[]
> = {
  queryKey: pricingKeys.options(),
  queryFn: getPricingOptions,
};

/**
 * Hook to fetch all available pricing options. Applies the per-currency
 * Phase 2 pricing-test cohort override (if any) to the response so that
 * downstream consumers (`useNewPricing`, `usePricingCards`, checkout) all see
 * cohort-aware prices and Stripe plan IDs without per-call wiring.
 *
 * @returns Query result containing an array of pricing options when successful
 */
export const usePricingOptions = () => {
  // Read targeting through `usePricingCohort` so SSR-supplied initial values
  // (via PricingCohortProvider) flow through here too — keeping the override
  // applied client-side aligned with what SSR baked into the prefetched data.
  const { currency, country, cohort } = usePricingCohort();

  // Memoise so TanStack Query's `select` returns a stable top-level array
  // reference across renders when (currency, country, cohort) is unchanged.
  // Without this, every render produces a new override result and downstream
  // consumers (`PricingCards`, `useNewPricing`) re-render unnecessarily.
  const select = useCallback(
    (data: PricingOption[]) =>
      applyPricingOverridesToApiResponse(data, { currency, country, cohort }),
    [currency, country, cohort]
  );

  return useQuery({ ...pricingQuery, select });
};
